import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, Check, MapPin, Phone, ShieldCheck, Clock, Sparkles, X } from "lucide-react";
import DemoRequest from "../components/sections/DemoRequest";
import PaymentMethods from "../components/sections/PaymentMethods";
import { CITIES, getCity, getServiceArea, getAlternative, getSeoSolution } from "../lib/seoLanding";
import { useI18n } from "../i18n/I18nProvider";

// JSON-LD helpers --------------------------------------------------------------
function buildLocalBusinessJson(city) {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `https://ojavana.com/pos/${city.slug}#business`,
    name: `O'javana — POS in ${city.name}`,
    url: `https://ojavana.com/pos/${city.slug}`,
    description: city.intro,
    telephone: "+41 32 510 06 20",
    email: "support@ojavana.com",
    priceRange: "CHF 39 – CHF 149 / month",
    areaServed: { "@type": "City", name: city.name },
    address: {
      "@type": "PostalAddress",
      addressLocality: city.name,
      addressRegion: city.canton,
      addressCountry: "CH",
    },
    geo: { "@type": "GeoCoordinates", latitude: city.lat, longitude: city.lon },
  };
}

function buildFaqJson(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((q) => ({
      "@type": "Question",
      name: q.q,
      acceptedAnswer: { "@type": "Answer", text: q.a },
    })),
  };
}

function Jsonld({ id, data }) {
  return (
    <script
      type="application/ld+json"
      data-testid={`jsonld-${id}`}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

function useDefaultFaqs() {
  const { t } = useI18n();
  return [
    { q: t("seo.faq_q1"), a: t("seo.faq_a1") },
    { q: t("seo.faq_q2"), a: t("seo.faq_a2") },
    { q: t("seo.faq_q3"), a: t("seo.faq_a3") },
  ];
}

// City pages -------------------------------------------------------------------
export function CityPage() {
  const { slug } = useParams();
  const { t, locale } = useI18n();
  const city = getCity(slug, locale);
  const FAQS = useDefaultFaqs();
  if (!city) return <Navigate to="/sitemap" replace />;
  return (
    <div data-testid={`city-page-${city.slug}`}>
      <Jsonld id={`localbusiness-${city.slug}`} data={buildLocalBusinessJson(city)} />
      <Jsonld id={`faq-${city.slug}`} data={buildFaqJson(FAQS)} />

      <section className="relative pt-20 pb-16 sm:pt-28 sm:pb-20 border-b border-zinc-200 overflow-hidden">
        <div className="absolute inset-0 technical-grid pointer-events-none" />
        <div className="container-oj relative">
          <div className="inline-flex items-center gap-2 rounded-full border border-oj-brand/40 bg-oj-brand/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-oj-deep">
            <MapPin className="size-3.5" /> {city.canton} · Switzerland
          </div>
          <h1 className="font-display mt-6 text-balance text-5xl sm:text-7xl font-black tracking-tighter leading-[0.92] text-oj-ink max-w-4xl">
            {city.hero}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-zinc-600 leading-relaxed">{city.intro}</p>
          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl">
            {city.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-zinc-800">
                <Check className="size-4 mt-0.5 shrink-0 text-oj-kiosk" />
                {b}
              </li>
            ))}
          </ul>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/#demo" data-testid="city-cta-demo" className="inline-flex items-center gap-1.5 bg-oj-brand text-oj-deep font-semibold px-6 py-3.5 rounded-md hover:bg-[#6FBDB9]">
              {t("seo.book_demo_in")} {city.name} <ArrowUpRight className="size-4" />
            </Link>
            <Link to="/callback" className="inline-flex items-center gap-1.5 border border-zinc-300 hover:border-zinc-900 text-oj-ink font-semibold px-6 py-3.5 rounded-md">
              <Phone className="size-4" /> {t("seo.request_callback")}
            </Link>
          </div>
        </div>
      </section>

      <SignalsRow />
      <FaqBlock items={FAQS} />
      <NearbyCities current={city.slug} />
      <DemoRequest />
    </div>
  );
}

// Service area pages -----------------------------------------------------------
export function ServiceAreaPage() {
  const { slug } = useParams();
  const { t, locale } = useI18n();
  const area = getServiceArea(slug, locale);
  const FAQS = useDefaultFaqs();
  if (!area) return <Navigate to="/sitemap" replace />;
  return (
    <div data-testid={`service-area-${area.slug}`}>
      <Jsonld id={`faq-area-${area.slug}`} data={buildFaqJson(FAQS)} />
      <section className="py-24 sm:py-32 border-b border-zinc-200 relative overflow-hidden">
        <div className="absolute inset-0 technical-grid pointer-events-none" />
        <div className="container-oj relative">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500"><MapPin className="size-3.5 inline -mt-0.5 mr-1 text-oj-brand" /> {t("seo.service_area")}</div>
          <h1 className="font-display mt-4 text-balance text-5xl sm:text-6xl font-black tracking-tighter leading-[0.95] text-oj-ink max-w-3xl">
            {area.name}
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-zinc-600">{area.summary}</p>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {area.cities.map((c) => (
              <div key={c} className="rounded-md border border-zinc-200 px-3 py-2.5 text-sm font-semibold text-zinc-800 bg-zinc-50">{c}</div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/#demo" data-testid="area-cta-demo" className="inline-flex items-center gap-1.5 bg-oj-ink text-white font-semibold px-6 py-3.5 rounded-md hover:bg-zinc-800">
              {t("seo.book_demo")} <ArrowUpRight className="size-4" />
            </Link>
            <Link to="/callback" className="inline-flex items-center gap-1.5 border border-zinc-300 hover:border-zinc-900 text-oj-ink font-semibold px-6 py-3.5 rounded-md">
              <Phone className="size-4" /> {t("seo.request_callback")}
            </Link>
          </div>
        </div>
      </section>
      <SignalsRow />
      <FaqBlock items={FAQS} />
      <DemoRequest />
    </div>
  );
}

// Alternatives -----------------------------------------------------------------
export function AlternativePage() {
  const { slug } = useParams();
  const { t, locale } = useI18n();
  const a = getAlternative(slug, locale);
  if (!a) return <Navigate to="/sitemap" replace />;
  const faq = [
    { q: t("seo.alt_faq_q1").replace("{competitor}", a.competitor), a: t("seo.alt_faq_a1").replace("{competitor}", a.competitor) },
    { q: t("seo.alt_faq_q2"), a: t("seo.alt_faq_a2") },
    { q: t("seo.alt_faq_q3"), a: t("seo.alt_faq_a3") },
  ];
  return (
    <div data-testid={`alt-${a.slug}`}>
      <Jsonld id={`faq-alt-${a.slug}`} data={buildFaqJson(faq)} />
      <section className="py-24 sm:py-32 border-b border-zinc-200 relative overflow-hidden">
        <div className="absolute inset-0 technical-grid pointer-events-none" />
        <div className="container-oj relative">
          <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white/70 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-700">
            <Sparkles className="size-3.5 text-oj-brand" /> {a.eyebrow}
          </div>
          <h1 className="font-display mt-6 text-balance text-5xl sm:text-7xl font-black tracking-tighter leading-[0.92] text-oj-ink max-w-4xl">{a.hero}</h1>
          <p className="mt-6 max-w-2xl text-lg text-zinc-600 leading-relaxed">{a.intro}</p>
        </div>
      </section>

      <section className="py-20 bg-zinc-50 border-b border-zinc-200">
        <div className="container-oj">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="rounded-2xl border-2 border-oj-kiosk p-7 bg-white" data-testid={`alt-${a.slug}-ours`}>
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-oj-kiosk">{t("seo.ours_label")}</div>
              <ul className="mt-5 space-y-3 text-zinc-800">
                {a.our_wins.map((p) => (
                  <li key={p} className="flex items-start gap-2"><Check className="size-4 mt-0.5 shrink-0 text-oj-kiosk" /> {p}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-zinc-200 p-7 bg-white" data-testid={`alt-${a.slug}-them`}>
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">{a.competitor} {t("seo.them_label_suffix")}</div>
              <ul className="mt-5 space-y-3 text-zinc-500">
                {a.competitor_pains.map((p) => (
                  <li key={p} className="flex items-start gap-2"><X className="size-4 mt-0.5 shrink-0 text-zinc-400" /> {p}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/#demo" data-testid={`alt-cta-${a.slug}`} className="inline-flex items-center gap-1.5 bg-oj-brand text-oj-deep font-semibold px-6 py-3.5 rounded-md hover:bg-[#6FBDB9]">
              {t("seo.switch_from")} {a.competitor} <ArrowUpRight className="size-4" />
            </Link>
            <Link to="/pricing" className="inline-flex items-center gap-1.5 border border-zinc-300 hover:border-zinc-900 text-oj-ink font-semibold px-6 py-3.5 rounded-md">
              {t("seo.see_pricing")}
            </Link>
          </div>
        </div>
      </section>

      <FaqBlock items={faq} />
      <DemoRequest />
    </div>
  );
}

// SEO solution pages -----------------------------------------------------------
export function SeoSolutionPage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const { t, locale } = useI18n();
  const s = getSeoSolution(slug, locale);
  const FAQS = useDefaultFaqs();
  if (!s) return <Navigate to="/sitemap" replace />;
  return (
    <div data-testid={`seo-${s.slug}`}>
      <Jsonld id={`faq-seo-${s.slug}`} data={buildFaqJson(FAQS)} />
      <section className="py-24 sm:py-32 border-b border-zinc-200 relative overflow-hidden">
        <div className="absolute inset-0 technical-grid pointer-events-none" />
        <div className="container-oj relative">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">{s.eyebrow}</div>
          <h1 className="font-display mt-4 text-balance text-5xl sm:text-7xl font-black tracking-tighter leading-[0.92] text-oj-ink max-w-4xl">{s.hero}</h1>
          <p className="mt-5 max-w-2xl text-lg text-zinc-600">{s.intro}</p>
          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl">
            {s.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-zinc-800">
                <Check className="size-4 mt-0.5 shrink-0 text-oj-kiosk" /> {b}
              </li>
            ))}
          </ul>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/#demo" data-testid={`seo-cta-${s.slug}`} className="inline-flex items-center gap-1.5 bg-oj-brand text-oj-deep font-semibold px-6 py-3.5 rounded-md hover:bg-[#6FBDB9]">
              {t("seo.book_demo")} <ArrowUpRight className="size-4" />
            </Link>
            <Link to="/configurator" className="inline-flex items-center gap-1.5 border border-zinc-300 hover:border-zinc-900 text-oj-ink font-semibold px-6 py-3.5 rounded-md">
              {t("seo.configure_hardware")}
            </Link>
          </div>
        </div>
      </section>
      {s.slug === "taptopay-terminals" && <PaymentMethods />}
      <FaqBlock items={FAQS} />
      <DemoRequest />
    </div>
  );
}

// Shared building blocks --------------------------------------------------------
function SignalsRow() {
  const { t } = useI18n();
  return (
    <section className="py-14 bg-zinc-50 border-b border-zinc-200">
      <div className="container-oj grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Signal icon={Clock} label={t("seo.signal_response")} value={t("seo.signal_response_value")} />
        <Signal icon={MapPin} label={t("seo.signal_onsite")} value={t("seo.signal_onsite_value")} />
        <Signal icon={ShieldCheck} label={t("seo.signal_hosted")} value={t("seo.signal_hosted_value")} />
      </div>
    </section>
  );
}

function Signal({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-5">
      <div className="flex items-center gap-2">
        <Icon className="size-4 text-oj-brand" />
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
      </div>
      <div className="mt-1 font-display text-xl font-bold text-oj-ink">{value}</div>
    </div>
  );
}

function FaqBlock({ items }) {
  const { t } = useI18n();
  return (
    <section className="py-20 bg-white border-b border-zinc-200" data-testid="faq-block">
      <div className="container-oj max-w-3xl">
        <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-oj-ink">{t("seo.faq_title")}</h2>
        <ul className="mt-8 space-y-4">
          {items.map((f) => (
            <li key={f.q} className="rounded-2xl border border-zinc-200 p-6 bg-zinc-50/60">
              <div className="font-display text-lg font-bold text-oj-ink">{f.q}</div>
              <p className="mt-2 text-zinc-700">{f.a}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function NearbyCities({ current }) {
  const { t, locale } = useI18n();
  const nearby = CITIES.filter((c) => c.slug !== current).slice(0, 6).map((c) => getCity(c.slug, locale));
  return (
    <section className="py-20 bg-zinc-50 border-b border-zinc-200">
      <div className="container-oj">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">{t("seo.other_cities")}</div>
        <div className="mt-6 flex flex-wrap gap-3">
          {nearby.map((c) => (
            <Link key={c.slug} to={`/pos/${c.slug}`} className="inline-flex items-center gap-1.5 rounded-md bg-white border border-zinc-200 hover:border-zinc-900 px-4 py-2.5 text-sm font-semibold text-oj-ink">
              {c.name} <ArrowUpRight className="size-3.5" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
